import React from "react";
import rough from "roughjs";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { Caption } from "./prims";
import type { Beat } from "./director";
import { TerminalActor } from "./visuals-code";
import { renderVisual, renderPhasedVisual } from "./visuals";
import { findMolecule, formulaEquation } from "./visuals-science";

/**
 * Anivaze v4 — Teacher pack: the chalkboard that WRITES.
 *
 * Khan-Academy signature: nothing slides in — content is written live, synced
 * to the voice. `ChalkText` reveals words with a moving left→right wipe + a
 * glowing chalk tip at the writing edge (the budget-honest version of per-stroke
 * glyphs; Hershey stroke fonts are a later upgrade). Underlines/circles are real
 * Rough.js paths that draw on. All pure functions of `t` — deterministic.
 */

const CHALK_CPS = 13; // handwriting pace, chars/sec

/** Average glyph width factor for the hand-font stack (estimate, no DOM). */
const CHAR_W = 0.62;

export interface BoardLine {
  text: string;
  color: string;
  size: number;   // font px
  start: number;  // writing begins
  end: number;    // fully written
  underline?: boolean;
}

/** Sequential writing schedule: each line starts after the previous finishes. */
export function scheduleBoard(
  lines: Array<{ text: string; color: string; size: number; underline?: boolean }>,
  begin = 0.35,
  gap = 0.4,
): BoardLine[] {
  const out: BoardLine[] = [];
  let cursor = begin;
  for (const l of lines) {
    const dur = Math.max(0.4, l.text.length / CHALK_CPS);
    out.push({ ...l, start: cursor, end: cursor + dur });
    cursor += dur + gap;
  }
  return out;
}

/** Writing progress 0..1 for a line at time t. */
export const lineProgress = (l: BoardLine, t: number): number =>
  t <= l.start ? 0 : t >= l.end ? 1 : (t - l.start) / (l.end - l.start);

/** Estimated pixel width of a line's text. */
export const lineWidth = (l: BoardLine): number => l.text.length * l.size * CHAR_W;

/** One chalk-written line: wipe reveal + glowing tip while writing. */
export const ChalkText: React.FC<{
  line: BoardLine; x: number; y: number; t: number; clipId: string; family: string; anchor?: "start" | "middle";
}> = ({ line, x, y, t, clipId, family, anchor = "start" }) => {
  const p = lineProgress(line, t);
  if (p <= 0) return null;
  const w = lineWidth(line);
  const x0 = anchor === "middle" ? x - w / 2 : x;
  const revealW = w * p;
  const writing = p < 1;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={x0 - 2} y={y - line.size * 1.1} width={revealW + 4} height={line.size * 1.6} />
        </clipPath>
      </defs>
      <text
        x={x0} y={y} clipPath={`url(#${clipId})`}
        fontFamily={family} fontWeight={700} fontSize={line.size}
        fill={line.color} opacity={0.94}
      >
        {line.text}
      </text>
      {writing && (
        <g>
          <circle cx={x0 + revealW} cy={y - line.size * 0.32} r={line.size * 0.34} fill={line.color} opacity={0.25} />
          <circle cx={x0 + revealW} cy={y - line.size * 0.32} r={line.size * 0.14} fill={line.color} opacity={0.9} />
        </g>
      )}
    </g>
  );
};

// Rough generator (module-level, seeded per call → deterministic, no boiling).
const rg = rough.generator();

/** A hand-drawn underline that draws on left→right after `start`. */
export const RoughUnderline: React.FC<{
  x: number; y: number; w: number; color: string; t: number; start: number; seed: number;
}> = ({ x, y, w, color, t, start, seed }) => {
  const p = t <= start ? 0 : Math.min(1, (t - start) / 0.4);
  if (p <= 0) return null;
  const drawable = rg.line(x, y, x + w, y + 3, { roughness: 1.6, seed, stroke: color, strokeWidth: 3 });
  const paths = rg.toPaths(drawable);
  // Reveal by scaling a clip window (path-length dasharray varies per rough path).
  return (
    <g>
      <defs>
        <clipPath id={`ul-${seed}`}>
          <rect x={x - 4} y={y - 10} width={(w + 8) * p} height={24} />
        </clipPath>
      </defs>
      <g clipPath={`url(#ul-${seed})`}>
        {paths.map((pa, i) => (
          <path key={i} d={pa.d} stroke={color} strokeWidth={2.6} fill="none" strokeLinecap="round" />
        ))}
      </g>
    </g>
  );
};

export interface BoardMeta { index: number; total: number }

/** Colors cycle like a teacher switching chalk. */
const CHALKS = ["#eee9dc", "#f5d76e", "#8fd0ff", "#9fe6a0", "#ffb3c7"];

/**
 * The Teacher-pack beat renderer: title written first (underlined), then each
 * point written line-by-line — synced across the beat's duration. Terminal
 * beats keep the real terminal (a teacher demoing commands on the projector).
 */
export function renderBoardBeat(beat: Beat, ctx: { layout: any; theme: Theme; t: number }, meta: BoardMeta): React.ReactNode {
  const { layout, theme, t } = ctx;
  const { content, caption, unit } = layout;
  const caps = <Caption box={caption} segs={beat.captions} t={t} theme={theme} unit={unit} />;

  if (beat.visual.kind === "terminal") {
    return (
      <>
        <ChalkText
          line={{ text: beat.title, color: CHALKS[1], size: unit * 4.6, start: 0.3, end: 0.3 + beat.title.length / CHALK_CPS }}
          x={content.cx} y={content.y + unit * 5} t={t} clipId={`bt-${meta.index}`} family={theme.display.family} anchor="middle"
        />
        <TerminalActor items={beat.visual.items} box={content as Box} theme={theme} unit={unit} t={Math.max(0, t - 0.8)} />
        {caps}
      </>
    );
  }

  const titleSize = unit * 5.2;
  const lineSize = unit * 3.4;
  // Board width constrains text; wrap long items onto their own budget.
  const maxChars = Math.floor((content.w * 0.92) / (lineSize * CHAR_W));
  const items = beat.visual.items
    .map((s) => (s.length > maxChars ? s.slice(0, maxChars - 1) + "…" : s))
    .slice(0, 3); // leave room: the board also DRAWS the diagram below

  // A teacher writes the formation equation before drawing the structure.
  const molSource =
    beat.visual.kind === "molecule"
      ? beat.visual.items.join(" ")
      : beat.visual.phases?.find((p) => p.kind === "molecule")?.items.join(" ");
  const mol = molSource ? findMolecule(molSource) : null;

  const lines = scheduleBoard([
    { text: beat.title, color: CHALKS[1], size: titleSize, underline: true },
    ...items.map((text, i) => ({ text: `• ${text}`, color: CHALKS[(i + 2) % CHALKS.length], size: lineSize })),
    ...(mol ? [{ text: formulaEquation(mol), color: CHALKS[1], size: lineSize * 1.15 }] : []),
  ]);

  const titleY = content.y + unit * 6;
  const lineGap = lineSize * 1.75;
  const linesBottom = titleY + unit * 4 + lines.length * lineGap;

  // The DIAGRAM, drawn on the board once the writing is under way (chalk colors
  // come from the chalk theme automatically). Phases play as a mini-story.
  const vTop = linesBottom + unit * 1.5;
  const vbox: Box = { x: content.x, y: vTop, w: content.w, h: Math.max(unit * 16, content.y + content.h - vTop), cx: content.cx, cy: vTop + Math.max(unit * 16, content.y + content.h - vTop) / 2 };
  const tVisual = Math.max(0, t - (lines[0]?.end ?? 0.6)); // starts after the title lands
  const hasStory = (beat.visual.phases?.length ?? 0) >= 2;
  const showVisual = beat.visual.kind !== "none" || hasStory;

  return (
    <>
      {lines.map((l, i) => {
        const isTitle = i === 0;
        const y = isTitle ? titleY : titleY + unit * 4 + i * lineGap;
        const x = isTitle ? content.cx : content.x + unit * 3;
        return (
          <g key={i}>
            <ChalkText
              line={l} x={x} y={y} t={t}
              clipId={`bd-${meta.index}-${i}`} family={theme.display.family}
              anchor={isTitle ? "middle" : "start"}
            />
            {l.underline && (
              <RoughUnderline
                x={content.cx - lineWidth(l) / 2} y={y + unit * 1.2} w={lineWidth(l)}
                color={CHALKS[1]} t={t} start={l.end + 0.15} seed={100 + meta.index * 7}
              />
            )}
          </g>
        );
      })}
      {showVisual && tVisual > 0 && (
        hasStory
          ? renderPhasedVisual(beat.visual.phases!, vbox, theme, unit, tVisual, Math.max(1, beat.dur - (lines[0]?.end ?? 0.6)))
          : renderVisual(beat.visual.kind, beat.visual.items, vbox, theme, unit, tVisual)
      )}
      {caps}
    </>
  );
}
